/*
  TKO product options / add-to-cart glue
  --------------------------------------
  Two jobs, both caused by the same thing: Kalles and the Qikify (Tepo) product
  options app each own part of the add-to-cart path and do not know about each other.

  1. DELEGATION (the important one)

     On products with Qikify options, Qikify hides the theme's in-page Add to cart
     (display:none) and renders its own button, name="tcustomizer-add". That button
     validates the required fields and does its own cart add, including Qikify's
     private _itemKey / _optionSetId properties.

     Kalles ALSO renders a second Add to cart in the sticky bottom bar, and that one
     submits the theme's form directly. It therefore bypasses Qikify completely: no
     required-field validation and no option data. On mobile the sticky bar is the most
     prominent button on the page, so this was a live ordering bug: a customer could add
     a uniform package with no size, no name and no numbers.

     Fix: when a cart form is submitted and a visible Qikify button exists for that
     product, cancel the submit and click Qikify's button instead. One add path, so
     validation and Qikify's own cart logic always apply.

  2. PROPERTY BRIDGE (fallback)

     Where no Qikify button exists, the theme's form is the real add path. Kalles scopes
     that <form> to the _product-buy-button block, so an app block mounted as a sibling
     has its properties[...] inputs outside the form and the browser never submits them.
     In that case we mirror the stray values into hidden inputs inside the form.

     Kept as a safety net for any product or future template where Qikify does not
     replace the button. Where Qikify already works this does nothing at all.

  Set window.TKO_BRIDGE_DEBUG = true in the console to log what it does.
*/
(function () {
  'use strict';

  var MIRROR_ATTR = 'data-tko-mirrored';
  var PROP_SELECTOR = '[name^="properties["]';
  var APP_BUTTON = '[name="tcustomizer-add"]';
  var delegating = false;
  var themeHandledAt = 0;

  /*
    3. TELLING THE THEME THE CART CHANGED

    The options app adds to cart with its own request, so Kalles never hears about it and
    the header count stays stale until a page refresh. <hdt-cart-count> only updates on
    'cart:count:update', which cascades from a 'cart:reload' on document: CartDrawer
    re-fetches /cart/update with its section and dispatches the count event. That handler
    passes actionAfterATC:"no_action", so this refreshes in place without opening the
    drawer.

    We watch for successful /cart/add requests we did not cause and fire that event. The
    watcher only observes, it never alters the request or the response, and the original
    promise is handed straight back to the caller.
  */
  function notifyThemeCartChanged() {
    document.dispatchEvent(new CustomEvent('cart:reload'));
    log('told the theme to reload the cart');
  }

  function isAddUrl(u) {
    return typeof u === 'string' && u.indexOf('/cart/add') !== -1;
  }

  /* The theme refreshes its own adds, so skip those and avoid a double render. */
  function shouldNotify() {
    return Date.now() - themeHandledAt > 3000;
  }

  function watchCartAdds() {
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = typeof input === 'string' ? input : (input && input.url) || '';
        var out = origFetch.apply(this, arguments);
        if (isAddUrl(url)) {
          try {
            out.then(function (res) {
              if (res && res.ok && shouldNotify()) setTimeout(notifyThemeCartChanged, 80);
            }, function () {});
          } catch (e) { /* never let the watcher break the request */ }
        }
        return out;
      };
    }

    var OrigXHR = window.XMLHttpRequest;
    if (OrigXHR && OrigXHR.prototype && OrigXHR.prototype.open) {
      var origOpen = OrigXHR.prototype.open;
      OrigXHR.prototype.open = function (method, url) {
        try {
          if (isAddUrl(url)) {
            this.addEventListener('load', function () {
              if (this.status >= 200 && this.status < 300 && shouldNotify()) {
                setTimeout(notifyThemeCartChanged, 80);
              }
            });
          }
        } catch (e) { /* ditto */ }
        return origOpen.apply(this, arguments);
      };
    }
  }

  watchCartAdds();

  function log() {
    if (window.TKO_BRIDGE_DEBUG && window.console) {
      console.log.apply(console, ['[tko-properties-bridge]'].concat([].slice.call(arguments)));
    }
  }

  function isCartAddForm(form) {
    if (!form || form.tagName !== 'FORM') return false;
    return (form.getAttribute('action') || '').indexOf('/cart/add') !== -1;
  }

  /* Stay inside the form's own section so a related product can never be involved. */
  function scopeFor(form) {
    return form.closest('.shopify-section') || form.closest('[id$="__main-product"]') || document;
  }

  /* The options app's own Add to cart for this product, if it is on the page. */
  function appButtonFor(form) {
    var btns = scopeFor(form).querySelectorAll(APP_BUTTON);
    for (var i = 0; i < btns.length; i++) {
      if (btns[i].offsetParent) return btns[i];
    }
    return null;
  }

  function usableValue(el) {
    if (!el.name) return null;
    /* an unticked box or an unselected radio has a value but no intent behind it */
    if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return null;
    if (el.disabled) return null;
    if (el.tagName === 'SELECT') {
      var opt = el.options[el.selectedIndex];
      return opt ? opt.value : null;
    }
    return el.value;
  }

  function bridge(form) {
    if (!isCartAddForm(form)) return;

    /* clear our own previous mirrors so a second submit does not stack duplicates */
    var old = form.querySelectorAll('input[' + MIRROR_ATTR + ']');
    for (var i = 0; i < old.length; i++) old[i].parentNode.removeChild(old[i]);

    /* names the form already carries win, we must not submit the same key twice */
    var claimed = {};
    var own = form.querySelectorAll(PROP_SELECTOR);
    for (var j = 0; j < own.length; j++) {
      if (own[j].name) claimed[own[j].name] = true;
    }

    var strays = scopeFor(form).querySelectorAll(PROP_SELECTOR);
    var mirrored = 0;

    for (var k = 0; k < strays.length; k++) {
      var el = strays[k];
      if (form.contains(el)) continue;          /* already submitted normally */
      if (claimed[el.name]) continue;           /* form owns this key */

      /*
        A product page can carry more than one cart form: Kalles renders a second
        one in the sticky bar. Anything sitting inside a DIFFERENT cart form is that
        form's business, including mirrors we added there on an earlier submit, which
        may now hold a stale value. Only genuinely form-less inputs get bridged.
      */
      var owner = el.closest('form[action*="/cart/add"]');
      if (owner && owner !== form) continue;

      var val = usableValue(el);
      if (val === null || String(val).trim() === '') continue;

      var hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = el.name;
      hidden.value = val;
      hidden.setAttribute(MIRROR_ATTR, '');
      form.appendChild(hidden);
      claimed[el.name] = true;
      mirrored++;
      log('mirrored', el.name, '=', val);
    }

    if (mirrored) log(mirrored + ' bridged into', form.id || form);
  }

  /*
    Capture phase on document so we get there before the theme's own submit handler,
    which Kalles binds on the form itself.
  */
  document.addEventListener('submit', function (ev) {
    var form = ev.target;
    if (!isCartAddForm(form)) return;

    /*
      Qikify's button is type="button" and does its own AJAX add, so it never fires a
      submit. Any submit we see here is the theme's path. The guard is belt and braces
      in case a future app version does submit the form.
    */
    var appBtn = delegating ? null : appButtonFor(form);
    if (appBtn) {
      ev.preventDefault();
      ev.stopImmediatePropagation();
      delegating = true;
      setTimeout(function () { delegating = false; }, 1500);
      log('handing this submit to the options app button');
      appBtn.click();
      return;
    }

    themeHandledAt = Date.now();
    bridge(form);
  }, true);

  /*
    Safety net for any code path that reads the form on click instead of firing submit.
    Skipped when the options app owns the add, so we do not leave unused inputs behind.
  */
  document.addEventListener('pointerdown', function (ev) {
    var t = ev.target;
    if (!t || !t.closest) return;
    var btn = t.closest('[name="add"], button[type="submit"], input[type="submit"]');
    if (!btn) return;
    var form = btn.form || btn.closest('form');
    if (!isCartAddForm(form)) return;
    if (appButtonFor(form)) return;
    bridge(form);
  }, true);
})();
